import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an element should hide based on page scroll position:
 * visible at the top, hidden past `threshold`. `show()` reveals it and
 * skips the next scroll check once, so a click-triggered reveal isn't
 * immediately undone by the scroll handler running on the same frame.
 */
export default function useHideOnScroll(threshold = 150) {
  const [isHidden, setIsHidden] = useState(false);
  const ignoreNextScrollRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (ignoreNextScrollRef.current) {
        ignoreNextScrollRef.current = false;
        return;
      }
      const top =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;

      if (top === 0) setIsHidden(false);
      if (top >= threshold) setIsHidden(true);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  const show = () => {
    ignoreNextScrollRef.current = true;
    setIsHidden(false);
  };

  return { isHidden, show };
}
